// Keyboard-operable 1-4 segmented control for one scorecard item. Built on
// native radio inputs (visually hidden, still focusable) so radiogroup
// semantics, arrow-key movement, and screen-reader announcement all come from
// the platform. The scale meaning is on each option (title + aria-label) and
// in the per-section legend.

import type { JSX } from 'react';

interface SegmentedScaleProps {
  itemId: string;
  /** The scorecard statement, used to label the group. */
  labelId: string;
  value: number | undefined;
  scaleLabels: Record<string, string>;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
}

export function SegmentedScale({
  itemId,
  labelId,
  value,
  scaleLabels,
  onChange,
  disabled,
}: SegmentedScaleProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div className="seg-group" role="radiogroup" aria-labelledby={labelId}>
        {[1, 2, 3, 4].map((option) => {
          const meaning = scaleLabels[String(option)] ?? '';
          return (
            <span className="seg-option" key={option} title={`${option}: ${meaning}`}>
              <input
                type="radio"
                name={itemId}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                disabled={disabled ?? false}
                aria-label={`${option}: ${meaning}`}
              />
              <span className="seg-face" aria-hidden="true">
                {option}
              </span>
            </span>
          );
        })}
      </div>
      {value !== undefined && !disabled && (
        <button
          type="button"
          className="seg-clear"
          onClick={() => onChange(undefined)}
          aria-label="Clear this answer"
        >
          clear
        </button>
      )}
    </div>
  );
}
